/**
 * Product quote request modal.
 *
 * - Refreshes hidden product/variant fields whenever the dialog opens, reading
 *   the currently selected variant id from the page's add-to-cart form and
 *   looking it up in an inline per-variant map rendered by Liquid.
 * - Submits via fetch to Shopify's native `contact` form endpoint so the
 *   customer never leaves the product page; renders its own success/error
 *   state client-side rather than waiting on a full page reload.
 * - Fires Klaviyo identify + track calls only if `window.klaviyo` exists
 *   (i.e. only if Klaviyo is actually installed) - a safe no-op otherwise.
 * - Never sets any marketing-consent field, so no automatic subscription
 *   happens regardless of whether Klaviyo is connected.
 */
class QuoteRequestForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.productId = this.dataset.productId;
    this.variantMap = this.#readVariantMap();

    const dialogComponent = this.closest('dialog-component');
    dialogComponent?.addEventListener('dialog:open', () => this.#syncVariantFields());

    this.form.addEventListener('submit', (event) => this.#handleSubmit(event));
  }

  #readVariantMap() {
    const script = document.querySelector(`[data-quote-variant-map="${this.productId}"]`);
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch {
      return {};
    }
  }

  #currentVariantId() {
    // The theme's standard add-to-cart form always carries the selected variant id.
    const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input?.value) return input.value;

    // Quote-only pages have no add-to-cart form: read the choice from the variant picker, or from the address it keeps up to date.
    const picked = document.querySelector('.variant-picker input[type="radio"]:checked[data-variant-id]');
    return picked?.dataset.variantId || new URLSearchParams(window.location.search).get('variant');
  }

  #syncVariantFields() {
    const variantId = this.#currentVariantId();
    const variant = variantId ? this.variantMap[variantId] : null;
    if (!variant) return;

    const setField = (key, value) => {
      const field = this.form.querySelector(`[data-quote-field="${key}"]`);
      if (field && value != null) field.value = value;
    };

    setField('variant', variant.title);
    setField('sku', variant.sku);
    setField('price', variant.price);
    setField('image', variant.image);
    setField('variant-id', variantId);
  }

  async #handleSubmit(event) {
    event.preventDefault();

    if (!this.form.reportValidity()) return;

    this.#syncVariantFields();
    const submittedAt = this.form.querySelector('[data-quote-field="submitted-at"]');
    if (submittedAt) submittedAt.value = new Date().toISOString();

    const submitButton = this.form.querySelector('.quote-request__submit');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Sending…';
    }

    const formData = new FormData(this.form);

    this.#trackKlaviyo(formData);

    try {
      await fetch(this.form.action, { method: 'POST', body: formData, credentials: 'same-origin' });
      this.#showSuccess();
    } catch {
      this.#showError();
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Send Quote Request';
      }
    }
  }

  #trackKlaviyo(formData) {
    if (typeof klaviyo === 'undefined') return;

    const email = formData.get('contact[email]');
    const phone = formData.get('contact[Phone Number]');
    const name = formData.get('contact[Full Name]');

    try {
      klaviyo.identify({
        email: email || undefined,
        phone_number: phone || undefined,
        first_name: name || undefined,
      });

      klaviyo.track('Product Quote Requested', {
        ProductTitle: formData.get('contact[Product Title]'),
        ProductSKU: formData.get('contact[Product SKU]'),
        SelectedVariant: formData.get('contact[Selected Variant]'),
        ProductURL: formData.get('contact[Product URL]'),
        ProductImage: formData.get('contact[Product Image]'),
        ProductPrice: formData.get('contact[Product Price]'),
        Quantity: formData.get('contact[Quantity]'),
        Postcode: formData.get('contact[Postcode]'),
        PreferredContactMethod: formData.get('contact[Preferred Contact Method]'),
        InstallationRequired: formData.get('contact[Installation Required]'),
      });
    } catch {
      // Klaviyo present but errored - never block the actual enquiry on this.
    }
  }

  #showSuccess() {
    const grid = this.form.querySelector('.quote-request__grid');
    const submitButton = this.form.querySelector('.quote-request__submit');
    grid?.remove();
    submitButton?.remove();

    const success = document.createElement('div');
    success.className = 'quote-request__success';
    // Counted as an enquiry by a custom pixel (see snippets/fsa-contact-bar.liquid).
    window.Shopify?.analytics?.publish?.('fsa_quote_request', { page: location.pathname });
    success.tabIndex = -1;
    success.innerHTML = `
      <div>
        <p class="quote-request__success-heading">Thank you — your quote request has been sent.</p>
        <p>One of our fireplace specialists will be in touch shortly to confirm pricing and availability.</p>
      </div>
    `;
    this.form.prepend(success);
    success.focus();
  }

  #showError() {
    let error = this.form.querySelector('.quote-request__error');
    if (!error) {
      error = document.createElement('div');
      error.className = 'quote-request__error';
      error.tabIndex = -1;
      this.form.prepend(error);
    }
    error.textContent = 'Something went wrong sending your request - please try again, or call us directly.';
    error.focus();
  }
}

if (!customElements.get('quote-request-form')) customElements.define('quote-request-form', QuoteRequestForm);
